import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { adminServiceCategoriesService } from '../services/admin.serviceCategories.service'
import type { ServiceCategoryDocument } from '@/types'

interface UseAdminServiceCategoriesResult {
  categories: Array<ServiceCategoryDocument & { id: string }>
  loading: boolean
  error: string | null
  createCategory: (title: string, order: number, serviceKey?: string) => Promise<string>
  updateCategory: (id: string, data: Partial<Pick<ServiceCategoryDocument, 'title' | 'serviceKey' | 'order'>>) => Promise<void>
  deleteCategory: (id: string) => Promise<void>
  refresh: () => void
}

export function useAdminServiceCategories(): UseAdminServiceCategoriesResult {
  const [categories, setCategories] = useState<Array<ServiceCategoryDocument & { id: string }>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => {
    setTick((t) => t + 1)
  }, [])

  useEffect(() => {
    setLoading(true)
    setError(null)
    adminServiceCategoriesService
      .getAllCategories()
      .then(setCategories)
      .catch((err: unknown) =>
        setError(formatUserFacingError(err, 'Failed to load categories'))
      )
      .finally(() => setLoading(false))
  }, [tick])

  const createCategory = useCallback(async (title: string, order: number, serviceKey?: string): Promise<string> => {
    const id = await adminServiceCategoriesService.createCategory(title, order, serviceKey)
    refresh()
    return id
  }, [refresh])

  const updateCategory = useCallback(
    async (id: string, data: Partial<Pick<ServiceCategoryDocument, 'title' | 'serviceKey' | 'order'>>): Promise<void> => {
      await adminServiceCategoriesService.updateCategory(id, data)
      refresh()
    },
    [refresh]
  )

  const deleteCategory = useCallback(async (id: string): Promise<void> => {
    await adminServiceCategoriesService.deleteCategory(id)
    refresh()
  }, [refresh])

  return { categories, loading, error, createCategory, updateCategory, deleteCategory, refresh }
}
